'use es6';

import { List, OrderedMap } from 'immutable';
import PropTypes from 'prop-types';
import { Component } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H7 } from 'foundations-components/transitional/components/H7';
import { H6 } from 'foundations-components/transitional/components/H6';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { List as FoundationsList } from 'foundations-components/transitional/components/List';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { MoveableObject } from 'foundations-components/transitional/components/MoveableObject';
import { ScrollingColumns } from 'foundations-components/transitional/components/ScrollingColumns';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import UISortableList from 'ui-addon-draggable/list/UISortableList';
import UIDragListItem from 'ui-addon-draggable/list/UIDragListItem';
import UIDragListHandle from 'ui-addon-draggable/draggable/UIDragListHandle';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { COLUMNS_HEIGHT_IN_PX, DIALOG_WIDTH_IN_PX } from 'ContentComponents/constants/ColumnSwitcher';
import withPartials from '../cmv2/decorators/withPartials';
import { stripHtmlFromString } from 'ContentComponents/components/cmv2/sanitize/sanitize';
import { capitalize } from 'ContentComponents/components/cmv2/utils/generic';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function langKey(key) {
  return `ContentComponents.columnSwitcher.columnSwitcherModal.${key}`;
}
function addColumn(columns, column) {
  const foundIdx = columns.findIndex(c => c === column);
  if (foundIdx > -1) {
    return columns;
  }
  return columns.push(column);
}
function removeColumn(columns, column) {
  const foundIdx = columns.findIndex(c => c === column);
  if (foundIdx > -1) {
    return columns.delete(foundIdx);
  }
  return columns;
}
function isValueAnObject(value) {
  // thank you stack overflow https://stackoverflow.com/questions/8511281/check-if-a-value-is-an-object-in-javascript/8511350#8511350
  return typeof value === 'object' && value !== null;
}
class ColumnSwitcherModal extends Component {
  constructor(props) {
    super(props);
    this.addColumn = this.addColumn.bind(this);
    this.handleCloseSelectedColumn = this.handleCloseSelectedColumn.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.propertySelected = this.propertySelected.bind(this);
    this.removeColumn = this.removeColumn.bind(this);
    this.renderProperty = this.renderProperty.bind(this);
    this.renderSelectedColumn = this.renderSelectedColumn.bind(this);
    this.setSelectedColumns = this.setSelectedColumns.bind(this);
    this.renderColumnKeyLabel = this.renderColumnKeyLabel.bind(this);
    this.state = {
      currentSearch: '',
      selectedColumns: new List()
    };
  }
  componentDidMount() {
    this.setSelectedColumns(this.props.currentCustomColumns);
  }
  componentDidUpdate(prevProps) {
    const {
      currentCustomColumns,
      open
    } = this.props;
    if (!prevProps.open && open) {
      this.setSelectedColumns(currentCustomColumns);
      this.setState({
        currentSearch: ''
      });
    }
  }
  setSelectedColumns(selectedColumns) {
    return Array.isArray(selectedColumns) ? this.setState({
      selectedColumns: List(selectedColumns)
    }) : this.setState({
      selectedColumns
    });
  }
  addColumn(column) {
    this.setState(prevState => {
      return {
        selectedColumns: addColumn(prevState.selectedColumns, column)
      };
    });
  }
  removeColumn(column) {
    this.setState(prevState => {
      return {
        selectedColumns: removeColumn(prevState.selectedColumns, column)
      };
    });
  }
  handleSubmit() {
    const {
      closeModal,
      onCustomColumnsSet,
      setCustomColumns
    } = this.props;
    const {
      selectedColumns
    } = this.state;
    const columnsToSet = selectedColumns.map(column => isValueAnObject(column) ? column.value : column);
    setCustomColumns(columnsToSet);
    if (typeof onCustomColumnsSet === 'function') {
      onCustomColumnsSet(columnsToSet);
    }
    closeModal();
  }
  handleCheckboxChange(column, e) {
    const {
      checked
    } = e.target;
    const {
      maxCustomColumns
    } = this.props;
    const {
      selectedColumns
    } = this.state;
    if (checked && selectedColumns.size === maxCustomColumns) {
      return;
    }
    if (checked) {
      this.addColumn(column);
    } else {
      this.removeColumn(column);
    }
  }
  handleCloseSelectedColumn(column) {
    this.removeColumn(column);
  }
  propertySelected(property) {
    return this.state.selectedColumns.contains(property);
  }
  renderLabel(label) {
    if (this.props.stripHtmlFromLabel) {
      return stripHtmlFromString(label);
    }
    return label;
  }
  renderColumnKeyLabel(columnKey) {
    let key = columnKey;
    if (isValueAnObject(columnKey)) {
      key = columnKey.label;
    }
    return capitalize(this.renderLabel(key));
  }
  renderProperty(columnKey = '', isIndented = false) {
    const checked = this.propertySelected(columnKey);
    const {
      columnsWithoutLangKey,
      getColumnLangKey,
      maxCustomColumns,
      partial,
      requiredColumnNames
    } = this.props;
    const {
      selectedColumns
    } = this.state;
    const disabled = !checked && selectedColumns.size === maxCustomColumns;
    const numRequiredColumns = requiredColumnNames.size;
    const maxColumns = numRequiredColumns + maxCustomColumns;
    return /*#__PURE__*/_jsx(View, {
      as: "span",
      paddingInlineStart: isIndented ? '400' : undefined,
      children: /*#__PURE__*/_jsx(Checkbox, {
        checked: checked,
        disabled: disabled,
        onChange: partial(this.handleCheckboxChange, columnKey),
        "data-test-column-key": columnKey,
        "data-test-id": `column-checkbox`,
        children: /*#__PURE__*/_jsx(Tooltip, {
          disabled: !disabled,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('tooManyColumns'),
            options: {
              maxColumns
            }
          }),
          placement: "right",
          children: columnsWithoutLangKey.includes(columnKey) ? /*#__PURE__*/_jsx("span", {
            children: this.renderColumnKeyLabel(columnKey)
          }) : /*#__PURE__*/_jsx(Text, {
            as: "span",
            "padding-inline-end": "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getColumnLangKey(columnKey)
            })
          })
        }, columnKey)
      })
    });
  }
  renderPropertySection(sectionKey, isFirst) {
    const {
      getColumnLangKey
    } = this.props;
    return /*#__PURE__*/_jsx(View, {
      paddingBlockStart: isFirst ? '200' : '500',
      children: /*#__PURE__*/_jsx(H7, {
        children: I18n.text(getColumnLangKey(sectionKey))
      })
    });
  }
  renderProperties() {
    const {
      allColumns,
      allColumnsWithSections
    } = this.props;
    if (allColumnsWithSections) {
      return /*#__PURE__*/_jsx(FoundationsList, {
        "data-test-id": "column-checkboxes",
        wrapChildren: true,
        children: allColumnsWithSections.entrySeq().toList().map(([key, value], index) => {
          return new List([key]).concat(value.filter(column => {
            if (isValueAnObject(column)) {
              return RegExp(this.state.currentSearch, 'i').test(column.label);
            }
            return RegExp(this.state.currentSearch, 'i').test(column);
          })).filter(Boolean).map(columnKey => {
            return columnKey === key ? this.renderPropertySection(key, index === 0) : this.renderProperty(columnKey, true);
          });
        })
      });
    } else {
      return /*#__PURE__*/_jsx(FoundationsList, {
        "data-test-id": "column-checkboxes",
        wrapChildren: true,
        children: allColumns.map(columnKey => this.renderProperty(columnKey)).toArray()
      });
    }
  }
  renderSelectedColumn(column) {
    const {
      columnsWithoutLangKey,
      getColumnLangKey,
      partial
    } = this.props;
    if (isValueAnObject(column)) {
      return /*#__PURE__*/_jsx(UIDragListItem, {
        dragId: column.label,
        children: /*#__PURE__*/_jsx(MoveableObject, {
          dragHandle: UIDragListHandle,
          onClose: partial(this.handleCloseSelectedColumn, column),
          "data-onboarding": `selected-column-${column.label}`,
          marginBlockEnd: "300",
          children: columnsWithoutLangKey.includes(column.label) ? /*#__PURE__*/_jsx("span", {
            children: this.renderLabel(column.label)
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: getColumnLangKey(column.label)
          })
        })
      }, column.value);
    }
    return /*#__PURE__*/_jsx(UIDragListItem, {
      dragId: column,
      children: /*#__PURE__*/_jsx(MoveableObject, {
        dragHandle: UIDragListHandle,
        onClose: partial(this.handleCloseSelectedColumn, column),
        "data-onboarding": `selected-column-${column}`,
        marginBlockEnd: "300",
        children: columnsWithoutLangKey.includes(column) ? /*#__PURE__*/_jsx("span", {
          children: this.renderLabel(column)
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: getColumnLangKey(column)
        })
      })
    }, column);
  }
  renderRequiredColumn(columnName) {
    return /*#__PURE__*/_jsx(MoveableObject, {
      closeable: false,
      draggable: false,
      marginBlockEnd: "300",
      children: columnName
    }, columnName);
  }
  renderSelectedColumns() {
    const {
      requiredColumnNames
    } = this.props;
    const {
      selectedColumns
    } = this.state;
    return /*#__PURE__*/_jsxs(View, {
      as: "section",
      paddingBlockStart: "400",
      children: [requiredColumnNames.map(this.renderRequiredColumn).toArray(), /*#__PURE__*/_jsx(UISortableList, {
        dragGroup: "unique-key",
        order: selectedColumns.toArray(),
        onOrderChange: this.setSelectedColumns,
        children: selectedColumns.map(this.renderSelectedColumn).toArray()
      })]
    });
  }
  renderBody() {
    const {
      body,
      hasSearch,
      maxColumnSelectionWarning,
      maxCustomColumns,
      requiredColumnNames
    } = this.props;
    const numSelectedColumns = this.state.selectedColumns.size;
    const numRequiredColumns = requiredColumnNames.size;
    const displayedPropertiesCount = numRequiredColumns > 0 ? numSelectedColumns + numRequiredColumns : numSelectedColumns;
    const showMaxColumnWarning = maxColumnSelectionWarning !== '' && numSelectedColumns >= maxCustomColumns;
    return /*#__PURE__*/_jsxs(DialogBody, {
      children: [showMaxColumnWarning && /*#__PURE__*/_jsx(Alert, {
        type: "info",
        marginBlockEnd: "400",
        children: maxColumnSelectionWarning
      }), body, /*#__PURE__*/_jsx(ScrollingColumns, {
        leftColumnHeader: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(H6, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: langKey('properties')
            })
          }), hasSearch && /*#__PURE__*/_jsx(SearchInput, {
            placeholder: I18n.text(langKey('search')),
            onChange: event => this.setState({
              currentSearch: event.target.value
            })
          })]
        }),
        leftColumnContent: this.renderProperties(),
        maxHeight: COLUMNS_HEIGHT_IN_PX,
        rightColumnHeader: /*#__PURE__*/_jsx(H6, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('selected'),
            options: {
              count: displayedPropertiesCount
            }
          })
        }),
        rightColumnContent: this.renderSelectedColumns(),
        rightColumnWidth: "50%",
        marginBlockStart: "400"
      })]
    });
  }
  render() {
    const {
      closeModal,
      open
    } = this.props;
    if (!open) {
      return null;
    }
    return /*#__PURE__*/_jsxs(Modal, {
      "data-test-id": "column-switcher-dialog",
      width: DIALOG_WIDTH_IN_PX,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: closeModal
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('header')
          })
        })]
      }), this.renderBody(), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          onClick: this.handleSubmit,
          "data-test-id": "modal-save",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('save')
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: closeModal,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('cancel')
          })
        })]
      })]
    });
  }
}
ColumnSwitcherModal.defaultProps = {
  allColumns: new List(),
  columnsWithoutLangKey: new List(),
  hasSearch: false,
  maxColumnSelectionWarning: '',
  open: false,
  requiredColumnNames: new List()
};
ColumnSwitcherModal.propTypes = {
  // List of all CUSTOMIZABLE column names
  allColumns: PropTypes.instanceOf(List),
  allColumnsWithSections: PropTypes.instanceOf(OrderedMap),
  body: PropTypes.node,
  columnsWithoutLangKey: PropTypes.instanceOf(List).isRequired,
  closeModal: PropTypes.func.isRequired,
  // List of column names in table
  currentCustomColumns: PropTypes.instanceOf(List).isRequired,
  // (String columnKey) -> String i18nLangKey
  getColumnLangKey: PropTypes.func.isRequired,
  hasSearch: PropTypes.bool.isRequired,
  // String value to show when the user has selected the maximum number of columns.
  // If empty string, no warning is shown.
  maxColumnSelectionWarning: PropTypes.string,
  maxCustomColumns: PropTypes.number.isRequired,
  // optional function that is called when columns are chosen
  onCustomColumnsSet: PropTypes.func,
  open: PropTypes.bool.isRequired,
  partial: PropTypes.func.isRequired,
  // List of non-customizable column/property names that will be shown, but not moveable
  requiredColumnNames: PropTypes.instanceOf(List).isRequired,
  // function that sets the columns in the table
  setCustomColumns: PropTypes.func.isRequired,
  stripHtmlFromLabel: PropTypes.bool
};
export default withPartials()(ColumnSwitcherModal);